import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// ─── localStorage helpers for member token ─────────────────────────────────
const MEMBER_TOKEN_KEY = 'memberToken';
const MEMBER_KEY = 'memberInfo';

function getMemberToken() { return localStorage.getItem(MEMBER_TOKEN_KEY); }
function getMemberInfo() {
  try { return JSON.parse(localStorage.getItem(MEMBER_KEY)); } catch { return null; }
}
function clearMemberSession() { localStorage.removeItem(MEMBER_TOKEN_KEY); localStorage.removeItem(MEMBER_KEY); }

function memberHeaders() {
  const token = getMemberToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─── Shared styles ──────────────────────────────────────────────────────────
const pageStyle = { minHeight: '100vh', backgroundColor: '#1a1a2e', color: '#ffffff', padding: '2rem 1rem' };
const cardStyle = { maxWidth: 420, margin: '0 auto', backgroundColor: '#16213e', borderRadius: 12, padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.4)' };
const btnPrimary = { width: '100%', padding: '12px', borderRadius: 6, border: 'none', backgroundColor: '#ff6800', color: '#fff', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' };
const btnOutline = { ...btnPrimary, backgroundColor: 'transparent', border: '1px solid #ff6800', color: '#ff6800' };

// ─── Stripe PaymentForm (rendered inside <Elements>) ────────────────────────
function PaymentForm({ onSuccess, amount }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message);
      setProcessing(false);
    } else if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      setError('Payment did not complete. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && <p style={{ color: '#ef4444', marginTop: 8, fontSize: '0.85rem' }}>{error}</p>}
      <button
        type="submit"
        disabled={!stripe || processing}
        style={{ ...btnPrimary, marginTop: 16, opacity: processing ? 0.7 : 1 }}
      >
        {processing ? 'Processing...' : `Pay $${Number(amount).toFixed(2)}`}
      </button>
    </form>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export default function PublicEventHistory() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantNum = Number(tenant) || null;

  // Data
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tenantName, setTenantName] = useState('');

  // Access gate
  const [requireLogin, setRequireLogin] = useState(false);
  const [accessFee, setAccessFee] = useState(0);
  const [gate, setGate] = useState('loading'); // loading | open | payment | ready
  const [member, setMember] = useState(getMemberInfo());

  // Payment
  const [clientSecret, setClientSecret] = useState(null);
  const [, setPaymentIntentId] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);

  // Venmo
  const [venmoConfig, setVenmoConfig] = useState(null);

  // ── Step 1: fetch tenant config ─────────────────────────────────────────
  useEffect(() => {
    if (!tenantNum) return;
    (async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/events/${tenantNum}/publicHistoryConfig`);
        if (res.data?.success) {
          setTenantName(res.data.tenantName || '');
          setRequireLogin(res.data.requireLogin);
          setAccessFee(res.data.accessFee || 0);

          if (!res.data.requireLogin) {
            setGate('open');
          } else if (getMemberToken()) {
            setGate('ready');
          } else {
            navigate(`/${tenant}/memberlogin`, { replace: true });
          }
        } else {
          setErr(res.data?.message || 'Event history is not available.');
          setGate('open');
        }
      } catch (e) {
        setErr(e.response?.status === 403
          ? 'This event history is not publicly available.'
          : 'Failed to load event history.');
        setGate('open');
      }
    })();
  }, [tenantNum]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Venmo config ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!tenantNum) return;
    (async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/venmo/public/${tenantNum}`);
        if (res.data?.success && res.data?.enabled) setVenmoConfig(res.data.venmo);
      } catch { /* Venmo not configured */ }
    })();
  }, [tenantNum]);

  // ── Step 2: fetch events when gate is open/ready ────────────────────────
  const fetchEvents = useCallback(async () => {
    if (!tenantNum) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(`${baseUrl}/api/events/${tenantNum}/publicHistory/events`, {
        headers: memberHeaders(),
      });
      if (res.data?.success) {
        setEvents(res.data.events || []);
        if (res.data.tenantName) setTenantName(res.data.tenantName);
      } else {
        setErr(res.data?.message || 'Event history is not available.');
      }
    } catch (e) {
      const status = e.response?.status;
      const data = e.response?.data;
      if (status === 401 && data?.requireLogin) {
        clearMemberSession();
        setMember(null);
        navigate(`/${tenant}/memberlogin`, { replace: true });
      } else if (status === 402 && data?.requirePayment) {
        setAccessFee(data.fee);
        setGate('payment');
      } else if (status === 403) {
        setErr('This event history is not publicly available.');
      } else {
        setErr('Failed to load event history.');
      }
    } finally {
      setLoading(false);
    }
  }, [tenantNum]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (gate === 'open' || gate === 'ready') fetchEvents();
  }, [gate, fetchEvents]);

  // ── Payment initiation ─────────────────────────────────────────────────
  useEffect(() => {
    if (gate !== 'payment' || clientSecret) return;
    (async () => {
      setPaymentBusy(true);
      try {
        const res = await axios.post(
          `${baseUrl}/api/members/${tenantNum}/member-pay-access`,
          {},
          { headers: memberHeaders() }
        );
        if (res.data?.alreadyPaid) {
          setGate('ready');
        } else if (res.data?.clientSecret) {
          setClientSecret(res.data.clientSecret);
          setPaymentIntentId(res.data.paymentIntentId);
        }
      } catch (e) {
        setErr(e.response?.data?.message || 'Payment setup failed');
        setGate('open');
      } finally {
        setPaymentBusy(false);
      }
    })();
  }, [gate, clientSecret, tenantNum]);

  const handlePaymentSuccess = async (piId) => {
    try {
      await axios.post(
        `${baseUrl}/api/members/${tenantNum}/member-confirm-payment`,
        { paymentIntentId: piId },
        { headers: memberHeaders() }
      );
      setGate('ready');
    } catch {
      setErr('Payment recorded but confirmation failed. Please refresh.');
    }
  };

  const handleLogout = () => {
    clearMemberSession();
    setMember(null);
    setEvents([]);
    navigate(`/${tenant}/memberlogin`, { replace: true });
  };

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => new Date(b.startdate || 0) - new Date(a.startdate || 0)),
    [events]
  );

  // ─── RENDER ─────────────────────────────────────────────────────────────

  if (gate === 'payment') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h2 style={{ textAlign: 'center', color: '#ff6800', marginBottom: '0.5rem' }}>
            {tenantName || 'Event History'}
          </h2>
          <p style={{ textAlign: 'center', color: '#aaa', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Access to event history requires a one-time payment of{' '}
            <strong style={{ color: '#fff' }}>${Number(accessFee).toFixed(2)}</strong>
          </p>
          {member && (
            <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.85rem', marginBottom: '1rem' }}>
              Signed in as {member.email}
            </p>
          )}
          {paymentBusy && <p style={{ textAlign: 'center', color: '#aaa' }}>Setting up payment...</p>}
          {clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night', variables: { colorPrimary: '#ff6800' } } }}>
              <PaymentForm onSuccess={handlePaymentSuccess} amount={accessFee} />
            </Elements>
          )}
          {!stripePromise && !paymentBusy && (
            <p style={{ textAlign: 'center', color: '#ef4444', fontSize: '0.85rem' }}>
              Payment is not configured. Please contact the event organizer.
            </p>
          )}
          <PoweredByFooter />
        </div>
      </div>
    );
  }

  if (gate === 'loading') {
    return (
      <div style={pageStyle}>
        <p style={{ textAlign: 'center', color: '#aaa' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '2rem', position: 'relative' }}>
          <h2 style={{ textAlign: 'center', color: '#ff6800', margin: 0 }}>
            {tenantName ? `${tenantName} — Event History` : 'Event History'}
          </h2>
          {member && (
            <div style={{ position: 'absolute', right: 0, display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => navigate(`/${tenant}/my-tickets`)}
                style={{ ...btnOutline, width: 'auto', padding: '6px 14px', fontSize: '0.8rem' }}
              >
                My Tickets
              </button>
              {requireLogin && (
                <button
                  type="button"
                  onClick={handleLogout}
                  style={{ ...btnOutline, width: 'auto', padding: '6px 14px', fontSize: '0.8rem' }}
                >
                  Sign Out
                </button>
              )}
            </div>
          )}
        </div>

        {loading && <p style={{ textAlign: 'center', color: '#aaa' }}>Loading...</p>}

        {err && (
          <div style={{ textAlign: 'center', padding: '2rem', backgroundColor: '#16213e', borderRadius: 8, color: '#ff6800' }}>
            {err}
          </div>
        )}

        {!loading && !err && sortedEvents.length === 0 && (
          <p style={{ textAlign: 'center', color: '#aaa' }}>No event history yet.</p>
        )}

        {!loading && !err && sortedEvents.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.25rem' }}>
            {sortedEvents.map((ev) => (
              <button
                key={ev.eventId}
                type="button"
                onClick={() => navigate(`/${tenant}/${ev.eventId}/publicEventArticle`)}
                style={{
                  textAlign: 'left', padding: 0, border: 'none', cursor: 'pointer',
                  backgroundColor: '#16213e', borderRadius: 12, overflow: 'hidden',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.35)', color: '#fff',
                }}
              >
                <div style={{ aspectRatio: '4/3', background: '#0f0f1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {ev.coverUrl ? (
                    ev.coverIsVideo ? (
                      <video src={ev.coverUrl} muted playsInline preload="metadata" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover', width: '100%', height: '100%' }} />
                    ) : (
                      <img src={ev.coverUrl} alt={ev.name} loading="lazy" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover', width: '100%', height: '100%' }} />
                    )
                  ) : (
                    <span style={{ color: '#556', fontSize: '0.85rem' }}>No cover image</span>
                  )}
                </div>
                <div style={{ padding: '0.85rem 1rem' }}>
                  <div style={{ fontWeight: 600, fontSize: '1rem' }}>{ev.name}</div>
                  {ev.startdate && (
                    <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: 2 }}>
                      {new Date(ev.startdate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {venmoConfig && <VenmoButton venmo={venmoConfig} />}

        <PoweredByFooter />
      </div>
    </div>
  );
}

function VenmoButton({ venmo }) {
  const venmoUrl = `https://venmo.com/${venmo.username.replace(/^@/, '')}` +
    (venmo.defaultAmount ? `?txn=pay&amount=${venmo.defaultAmount}` : '?txn=pay') +
    (venmo.defaultNote ? `&note=${encodeURIComponent(venmo.defaultNote)}` : '');

  return (
    <div style={{ textAlign: 'center', margin: '2rem auto', padding: '1.5rem', backgroundColor: '#16213e', borderRadius: 12, maxWidth: 400 }}>
      <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '1rem' }}>
        {venmo.defaultNote || `Pay ${venmo.displayName}`}
      </p>
      <a
        href={venmoUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '12px 28px',
          backgroundColor: '#008CFF', color: '#fff', fontWeight: 700, fontSize: '1rem',
          borderRadius: 8, textDecoration: 'none', border: 'none', cursor: 'pointer',
        }}
      >
        <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff">
          <path d="M19.135 1.755c.8 1.32 1.16 2.68 1.16 4.4 0 5.48-4.68 12.6-8.48 17.6H4.8L2.16 2.395l6.24-.6 1.56 12.52c1.44-2.36 3.24-6.08 3.24-8.64 0-1.64-.28-2.76-.72-3.68l6.655-.245z" />
        </svg>
        Pay with Venmo
        {venmo.defaultAmount ? ` — $${Number(venmo.defaultAmount).toFixed(2)}` : ''}
      </a>
      <p style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.75rem' }}>
        Opens Venmo to pay {venmo.displayName}
      </p>
    </div>
  );
}

function PoweredByFooter() {
  return (
    <div style={{ textAlign: 'center', marginTop: '3rem', paddingTop: '1.5rem', borderTop: '1px solid #333', color: '#666', fontSize: '0.85rem' }}>
      Powered by{' '}
      <a href="https://on-screen.app" target="_blank" rel="noopener noreferrer" style={{ color: '#ff6800', textDecoration: 'none' }}>
        On-Screen
      </a>
    </div>
  );
}
